import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Building2, Copy, Globe, Lock, Share2, UserRound } from 'lucide-react';
import {
  type DocumentDetail,
  type DocumentShareAudience,
  type ProjectDocumentShare,
  type ProjectDocumentShareState,
  type ProjectDocumentShareVersionMode,
  PROJECT_DOCUMENT_SHARE_VERSION_MODES,
} from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { useConfirmPopover } from '../../../components/ui/confirm-popover';
import { GlassSegmented } from '../../../components/ui/glass-segmented';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Skeleton } from '../../../components/ui/states';
import { copyText } from '../../../lib/clipboard';
import { displayDate } from '../../../lib/date';
import { errorMessage } from '../../../lib/error-message';
import { cn } from '../../../lib/utils';
import { documentShareApi, documentShareKeys, uploadShareCopies } from './document-share-api';

const AUDIENCES = ['org', 'chillpm', 'public'] as const satisfies readonly DocumentShareAudience[];

const AUDIENCE_ICON = { org: Building2, chillpm: UserRound, public: Globe } as const;

/**
 * Nhãn "đang chia sẻ ở cấp nào" — APM-PDSH-b. Đặt ở dòng meta của khung xem trước; chưa chia sẻ
 * thì không vẽ gì (nút Chia sẻ mang ổ khoá đã nói điều đó).
 */
export function DocumentShareBadge({
  share,
}: {
  readonly share: ProjectDocumentShare | null | undefined;
}): ReactNode {
  const { t } = useTranslation();
  if (!share) return null;
  const Icon = AUDIENCE_ICON[share.audience];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
        share.audience === 'public'
          ? 'border-rag-amber/40 bg-rag-amber/10 text-rag-amber'
          : 'border-primary/30 bg-primary/10 text-primary',
      )}
      title={t('documents.share.badgeTitle', {
        audience: t(`documents.share.audience.${share.audience}`),
        version: versionLabel(t, share),
      })}
    >
      <Icon aria-hidden className="size-3" />
      {t(`documents.share.audience.${share.audience}`)}
    </span>
  );
}

function versionLabel(
  t: ReturnType<typeof useTranslation>['t'],
  share: Pick<ProjectDocumentShare, 'versionMode' | 'versionNo'>,
): string {
  return share.versionMode === 'version'
    ? t('documents.share.versionPinned', { no: share.versionNo })
    : t(`documents.share.versionMode.${share.versionMode}`);
}

/** Trạng thái chia sẻ của một tài liệu — dùng chung giữa nút và nhãn ở dòng meta. */
export function useDocumentShare(documentId: string) {
  return useQuery({
    queryKey: documentShareKeys.state(documentId),
    queryFn: () => documentShareApi.state(documentId),
    // Lượt mở mới phải hiện khi mở lại khung (cùng lý do `staleTime: 0` của APM-SHR-e).
    staleTime: 0,
  });
}

/**
 * Nút **Chia sẻ** trên thanh nút của khung xem trước tài liệu dự án — APM-PDSH-b.
 *
 * Chỉ xem, không bao giờ sửa. Một thiết lập mỗi tài liệu (người dùng chốt 2026-10-14): ba mức
 * đối tượng × bốn cách chọn phiên bản. Người có quyền duyệt đổi được; thành viên khác thấy trạng
 * thái và chép được link.
 *
 * ## Bản sạch
 *
 * `.chilldoc` mang bình luận trong tệp, nên người nhận chỉ đọc **bản sạch** do trình duyệt người
 * trong dự án dựng (luật #3). Mỗi lần trạng thái báo còn `pendingCopies` — vừa bật chia sẻ, vừa
 * Lưu, vừa duyệt — và người đang xem sửa được tài liệu (`canWrite`), nút tự dựng và tải chúng lên.
 * Khoá theo danh sách tệp còn thiếu nên một lần hỏng không thành vòng lặp; người dùng thử lại bằng
 * nút trong khung.
 */
export function DocumentShareButton({
  doc,
  canWrite,
}: {
  readonly doc: DocumentDetail;
  readonly canWrite: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const key = documentShareKeys.state(doc.id);
  const state = useDocumentShare(doc.id);
  const share = state.data?.share ?? null;

  const absorb = (next: ProjectDocumentShareState | null) => {
    if (next) queryClient.setQueryData(key, next);
  };

  const copies = useMutation({
    mutationFn: () => uploadShareCopies(doc.id, share?.pendingCopies ?? []),
    onSuccess: absorb,
  });
  const pendingKey = share?.pendingCopies.map((item) => item.fileId).join(',') ?? '';
  const attempted = useRef<string | null>(null);
  useEffect(() => {
    if (!canWrite || !pendingKey || attempted.current === pendingKey) return;
    attempted.current = pendingKey;
    copies.mutate();
    // `copies` đổi danh tính mỗi lần dựng; khoá thật là danh sách tệp còn thiếu.
  }, [canWrite, pendingKey]);

  const Icon = share ? AUDIENCE_ICON[share.audience] : Lock;
  const label = share
    ? t('documents.share.buttonShared', {
        audience: t(`documents.share.audience.${share.audience}`),
      })
    : t('documents.share.button');

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="relative size-8"
          aria-label={label}
          title={label}
        >
          <Share2 aria-hidden />
          {share ? (
            <Icon
              aria-hidden
              className="absolute bottom-0.5 right-0.5 size-3 rounded-full bg-background text-primary"
            />
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-3">
        {state.isPending ? (
          <Skeleton className="h-40" />
        ) : state.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage(state.error, t)}
          </p>
        ) : (
          <SharePanel
            doc={doc}
            state={state.data}
            onChange={absorb}
            copying={copies.isPending}
            copyFailed={copies.isError}
            onRetryCopies={canWrite ? () => copies.mutate() : undefined}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

function SharePanel({
  doc,
  state,
  onChange,
  copying,
  copyFailed,
  onRetryCopies,
}: {
  readonly doc: DocumentDetail;
  readonly state: ProjectDocumentShareState;
  readonly onChange: (next: ProjectDocumentShareState) => void;
  readonly copying: boolean;
  readonly copyFailed: boolean;
  readonly onRetryCopies: (() => void) | undefined;
}): ReactNode {
  const { t } = useTranslation();
  const { share, canManage } = state;
  const confirm = useConfirmPopover();

  const [audience, setAudience] = useState<DocumentShareAudience>(share?.audience ?? 'org');
  const [versionMode, setVersionMode] = useState<ProjectDocumentShareVersionMode>(
    share?.versionMode ?? 'latest',
  );
  const [versionNo, setVersionNo] = useState<number>(share?.versionNo ?? doc.latestVersionNo);

  const dirty =
    !share ||
    share.audience !== audience ||
    share.versionMode !== versionMode ||
    (versionMode === 'version' && share.versionNo !== versionNo);

  // `lib/clipboard.ts`: mở bằng IP LAN thì không có `navigator.clipboard`.
  const copyLink = async (url: string) => {
    if (await copyText(url)) toast.success(t('documents.share.copied'));
    else toast.error(t('documents.share.copyFailed'));
  };

  const save = useMutation({
    mutationFn: () =>
      documentShareApi.set(doc.id, {
        audience,
        versionMode,
        ...(versionMode === 'version' ? { versionNo } : {}),
      }),
    onSuccess: async (next) => {
      onChange(next);
      // Bật lần đầu ⇒ tự chép link, như "Tạo link chia sẻ" của Tệp (APM-SHR-e).
      if (!share && next.share) await copyLink(next.share.url);
      else toast.success(t('documents.share.saved'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const revoke = useMutation({
    mutationFn: () => documentShareApi.revoke(doc.id),
    onSuccess: (next) => {
      onChange(next);
      toast.success(t('documents.share.revoked'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const pending = share?.pendingCopies.length ?? 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <p className="font-medium">{t('documents.share.title')}</p>
        <p className="text-xs text-muted-foreground">{t('documents.share.viewOnly')}</p>
      </div>

      <p
        className={cn(
          'flex items-center gap-2 rounded-DEFAULT border px-2.5 py-1.5 text-sm',
          share ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/40',
        )}
        data-testid="document-share-status"
      >
        {share ? (
          <>
            <DocumentShareBadge share={share} />
            <span className="text-muted-foreground">{versionLabel(t, share)}</span>
          </>
        ) : (
          <>
            <Lock aria-hidden className="size-3.5 text-muted-foreground" />
            {t('documents.share.notShared')}
          </>
        )}
      </p>

      {canManage ? (
        <>
          <Field label={t('documents.share.audienceLabel')}>
            <GlassSegmented
              ariaLabel={t('documents.share.audienceLabel')}
              value={audience}
              onChange={setAudience}
              size="sm"
              options={AUDIENCES.map((value) => ({
                value,
                label: t(`documents.share.audienceShort.${value}`),
                title: t(`documents.share.audienceHelp.${value}`),
              }))}
            />
            <p className="text-xs text-muted-foreground">
              {t(`documents.share.audienceHelp.${audience}`)}
            </p>
          </Field>

          <Field label={t('documents.share.versionLabel')}>
            <div role="radiogroup" className="flex flex-col gap-1">
              {PROJECT_DOCUMENT_SHARE_VERSION_MODES.map((mode) => (
                <label
                  key={mode}
                  className="flex cursor-pointer items-start gap-2 rounded-DEFAULT px-1.5 py-1 text-sm hover:bg-muted/50"
                >
                  <input
                    type="radio"
                    name={`share-version-${doc.id}`}
                    className="mt-1"
                    checked={versionMode === mode}
                    onChange={() => setVersionMode(mode)}
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span>{t(`documents.share.versionMode.${mode}`)}</span>
                    <span className="text-xs text-muted-foreground">
                      {mode === 'latest_approved' &&
                      doc.approvedVersionNo === null &&
                      doc.status !== 'approved'
                        ? t('documents.share.noApprovedYet')
                        : t(`documents.share.versionModeHelp.${mode}`)}
                    </span>
                    {mode === 'version' && versionMode === 'version' ? (
                      <select
                        aria-label={t('documents.share.pickVersion')}
                        className="mt-1 h-8 rounded-[12px] border border-input bg-background px-2 text-sm"
                        value={versionNo}
                        onChange={(event) => setVersionNo(Number(event.target.value))}
                      >
                        {doc.versions.map((version) => (
                          <option key={version.versionNo} value={version.versionNo}>
                            {t('documents.share.versionOption', {
                              no: version.versionNo,
                              date: displayDate(version.createdAt),
                            })}
                            {version.isApproved ||
                            (doc.status === 'approved' && version.versionNo === doc.latestVersionNo)
                              ? ` · ${t('documents.status.approved')}`
                              : ''}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </span>
                </label>
              ))}
            </div>
          </Field>
        </>
      ) : (
        <p className="text-xs text-muted-foreground">{t('documents.share.managersOnly')}</p>
      )}

      {share ? (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5">
            <input
              readOnly
              aria-label={t('documents.share.link')}
              value={share.url}
              className="h-8 min-w-0 flex-1 rounded-[12px] border border-input bg-muted/30 px-2 text-xs"
              onFocus={(event) => event.currentTarget.select()}
            />
            <Button size="sm" variant="outline" onClick={() => void copyLink(share.url)}>
              <Copy aria-hidden className="size-3.5" />
              {t('documents.share.copy')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('documents.share.opens', { count: share.openCount })}
          </p>
          {pending > 0 ? (
            <p
              role="status"
              className="flex flex-wrap items-center gap-2 rounded-DEFAULT border border-rag-amber/40 bg-rag-amber/5 px-2 py-1.5 text-xs"
            >
              {copying
                ? t('documents.share.preparing', { count: pending })
                : copyFailed
                  ? t('documents.share.preparingFailed', { count: pending })
                  : t('documents.share.pending', { count: pending })}
              {onRetryCopies && !copying ? (
                <Button size="sm" variant="ghost" className="h-6 px-2" onClick={onRetryCopies}>
                  {t('documents.share.retry')}
                </Button>
              ) : null}
            </p>
          ) : null}
        </div>
      ) : null}

      {canManage ? (
        <div className="flex items-center justify-between gap-2">
          {share ? (
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive"
              disabled={revoke.isPending}
              onClick={() =>
                confirm.ask({
                  message: t('documents.share.revokeConfirm'),
                  confirmLabel: t('documents.share.revoke'),
                  onConfirm: () => revoke.mutate(),
                })
              }
            >
              {t('documents.share.revoke')}
            </Button>
          ) : (
            <span />
          )}
          <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {share ? t('documents.share.save') : t('documents.share.enable')}
          </Button>
        </div>
      ) : null}
      {confirm.popover}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}
